import { recipeWriteVersion } from '../../../../services/recipeWriteConflict';
import { useCostPriceStore } from "../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../stores/appSubrecipesStore";
import { ReviewIngredient } from "../../../../types/reviewTypes";
import {
    CostPriceAnalysis,
    CostPriceIngredient,
    fetchCostPriceAnalysisById,
    toIngredientUpdatePayload,
    updateCostPriceAnalysis,
} from "../../../../services/costPrice/costPriceService";
import {
    Subrecipe,
    SubrecipeIngredient,
    SubrecipeIngredientInput,
    fetchSubrecipeById,
    updateSubrecipe,
} from "../../../../services/subrecipes/subrecipesService";
import { productKey } from "../../../../utils/reports/shared";
import { templateQuantityInUnit } from "./reviewUtils";
import {
    alreadyOnCandidate,
    candidateUnit,
    MatchingIngredientRef,
    ReviewWriteAdapter,
    SwapCandidate,
} from "./reviewAdapter";

/**
 * The write side of the CUSTOM INGREDIENT LINK flow: re-pointing one placeholder
 * row (a custom ingredient the user typed before the product ever arrived in
 * order history) at a real order-history product or a subrecipe.
 *
 * Unlike `costPriceReviewAdapter`, there is no automation review report to walk —
 * the "other places this ingredient is used" list is derived from the LIVE cost
 * model (every other analysis + every subrecipe) instead. Everything that decides
 * what gets written is a pure function below; the factory at the bottom is the
 * only part that touches stores and the network.
 */

/** The subset of a cost price / subrecipe ingredient row this flow reads. */
export interface LinkableCustomIngredient {
    productName?: string;
    articleId?: string;
    platform?: string;
    customPlatformName?: string;
    quantityNeeded?: number;
    quantityInfo?: {
        stdAmount: number;
        stdMultiplier: number;
        stdUnit: string;
    };
    /** Set when the row is sourced from a SIBLING restaurant — see `sameSourcingContext`. */
    sourceRestaurantId?: string;
}

/** Which record an `itemId` addresses — the two domains a custom ingredient lives in. */
export type CustomLinkItemKind = 'costPrice' | 'subrecipe';

/** A live use of the same custom ingredient somewhere else, plus what it takes to write it. */
export interface CustomLinkMatch extends MatchingIngredientRef {
    kind: CustomLinkItemKind;
    /** Row id inside that item's own ingredient array, at the time the list was built. */
    rowId: string;
}

/**
 * Present a live custom ingredient row to the shared swap picker. The three
 * `template*` fields exist for automation reports; for a live row the template
 * IS the row, so they mirror it — that is what makes "Suggested" search on the
 * custom ingredient's own name and what makes the quantity carry over.
 *
 * `SwapIngredientPanel` reads exactly eight fields off its `ingredient` prop
 * (`grep -o "ingredient\.[a-zA-Z]*"`): templateName, templateQuantity,
 * templateUnit, status, quantityNeeded, quantityInfo, alternatives,
 * subrecipePortionsNeeded. All are supplied here; `subrecipePortionsNeeded` is
 * correctly absent because a custom row is never a subrecipe reference and the
 * panel only reads it on `status === 'subrecipe'`.
 */
export function toReviewIngredient(ing: LinkableCustomIngredient): ReviewIngredient {
    return {
        templateName: ing.productName ?? '',
        templateQuantity: ing.quantityNeeded ?? 0,
        templateUnit: ing.quantityInfo?.stdUnit ?? '',
        status: 'matched',
        productName: ing.productName,
        articleId: ing.articleId,
        platform: ing.platform,
        quantityNeeded: ing.quantityNeeded,
        quantityInfo: ing.quantityInfo,
        isCustom: true,
        customPlatformName: ing.customPlatformName,
        // Alternatives are stripped from custom rows on write, so a custom row never
        // has runner-ups. The panel only PREPENDS them to the Suggested list, which is
        // then filled from the fuzzy passes — an empty array costs nothing else.
        alternatives: [],
    };
}

/**
 * Canonical product identity of a custom row — the frontend twin of the server's
 * `canonicalProductKey` (`domains/costPrice/costModelProductKeys.ts`). Never compare
 * custom ingredients by raw name: `resolveProductIdentifier` folds sentinel article
 * ids and `platformsWithoutArticleId` platforms onto the normalized name, and a
 * bare name comparison would both over- and under-match (dataRules rule 8).
 * Null when the row carries no usable identity.
 */
export function linkIdentityKey(ing: LinkableCustomIngredient): string | null {
    if (!ing.platform) return null;
    const key = productKey(ing.platform, ing.articleId, ing.productName);
    return key.endsWith('::') ? null : key;
}

/**
 * The pre-filled quantity for a row being re-pointed at `targetUnit`, mirroring
 * `SwapIngredientPanel.defaultQuantityFor` exactly (README trap 3):
 *  1. same std unit  → carry the row's own quantity over unchanged;
 *  2. same unit TYPE → convert (kg↔g, L↔ml);
 *  3. different type or unknown → a per-unit default, which is a DEFAULT, not a
 *     derivation — every caller must leave it editable and visible.
 */
export function defaultLinkQuantity(
    quantityNeeded: number | undefined,
    sourceUnit: string | undefined,
    targetUnit: string | undefined
): number {
    if (quantityNeeded && quantityNeeded > 0 && sourceUnit && sourceUnit === targetUnit) {
        return quantityNeeded;
    }
    const converted = templateQuantityInUnit(quantityNeeded ?? 0, sourceUnit ?? '', targetUnit);
    if (converted !== null) return converted;
    // Unit.GRAM / Unit.MILLILITER — inlined so this module stays store-free.
    return targetUnit === 'g' || targetUnit === 'ml' ? 100 : 1;
}

/**
 * True when re-pointing from `sourceUnit` to `targetUnit` crosses a unit TYPE boundary
 * (g ↔ ml ↔ p), or either unit is unknown — exactly the case where `defaultLinkQuantity`
 * falls back to its 100/1 DEFAULT instead of carrying or converting the amount.
 *
 * This is the dataRules §10 silent-wrong-cost case, so the flows that can produce it say
 * so out loud. Shares `templateQuantityInUnit`'s type table rather than restating it: a
 * null conversion of a positive amount means precisely "different or unknown type".
 */
export function isUnitTypeChange(sourceUnit: string | undefined, targetUnit: string | undefined): boolean {
    if (!sourceUnit || !targetUnit) return true;
    if (sourceUnit.toLowerCase().trim() === targetUnit.toLowerCase().trim()) return false;
    return templateQuantityInUnit(1, sourceUnit, targetUnit) === null;
}

/**
 * True when the picker had to INVENT this candidate's pack size — `productCandidate` defaults a
 * missing `quantityInfo` to `{stdAmount: 1, stdMultiplier: 1, stdUnit: 'p'}`
 * (`SwapIngredientPanel.tsx:286`), so the whole line price is counted as one "piece".
 *
 * Detectable without touching the panel, and without a false positive on a product that genuinely
 * IS sold per piece: the same factory computes `unitPriceLabel` from the RAW `product.quantityInfo`
 * (`:288`), and `calculateUnitPrice` returns undefined exactly when that raw value is missing or has
 * a falsy `stdAmount` / `stdMultiplier` / `stdUnit` (`orderhistory/aggregations.ts:340`) — the same
 * degenerate shapes the server plan demotes. A real 1×1 piece product yields a label; an invented
 * one cannot. A candidate with no `price` at all is indistinguishable and is not flagged.
 *
 * Pre-existing panel behaviour that this feature makes reachable from every custom row, so the
 * flow surfaces it rather than changing the shared default.
 */
export function hasInventedPackSize(candidate: SwapCandidate): boolean {
    if (candidate.kind !== 'product') return false;
    return candidate.price !== undefined && candidate.unitPriceLabel === undefined;
}

/**
 * The usages whose quantity would be a DEFAULT rather than a carried or converted amount if
 * the row were bound to `targetUnit` — i.e. exactly the `defaultLinkQuantity` third branch,
 * per usage against that usage's OWN frozen unit.
 *
 * Derived from the target the user actually bound, never from a precomputed server flag: a
 * plan's `unitChange` is calculated against its `best` candidate, so it says nothing once the
 * user binds a different one, and it can be absent when the amount really was guessed. A
 * defaulted amount that is not on screen before commit is the dataRules §10 silent-wrong-cost
 * failure, so the flows that can produce one compute it themselves.
 */
export function usagesWithDefaultedQuantity<T extends { stdUnit: string }>(
    usages: T[],
    targetUnit: string | undefined
): T[] {
    if (!targetUnit) return usages;
    return usages.filter(usage => isUnitTypeChange(usage.stdUnit, targetUnit));
}

/** A cost price analysis, reduced to what the scan needs. */
export type ScannableAnalysis = Pick<CostPriceAnalysis, 'id' | 'costPriceName' | 'ingredients'>;
/** A subrecipe, reduced to what the scan needs. */
export type ScannableSubrecipe = Pick<Subrecipe, 'id' | 'name' | 'ingredients'>;

/**
 * Two rows share a sourcing context when they price against the same restaurant's data.
 * `(articleId, platform)` alone is NOT enough: a custom ingredient's article id is typed by
 * hand, so two restaurants can carry the same one, and a fan-out matched on identity alone
 * would re-home a sibling-sourced row onto this restaurant's product. The user confirmed
 * ONE row, so only rows in the same context are offered.
 */
function sameSourcingContext(a: LinkableCustomIngredient, b: LinkableCustomIngredient): boolean {
    return (a.sourceRestaurantId ?? '') === (b.sourceRestaurantId ?? '');
}

export interface FindCustomIngredientUsesParams {
    /** `linkIdentityKey` of the custom row being linked. */
    identityKey: string;
    /** The analysis / subrecipe the link started from. */
    sourceItemId: string;
    /**
     * The row that was just linked. Only THAT row is excluded, not its whole item: a dish can
     * carry the same custom ingredient on two separate rows, and the second one is a genuine
     * fan-out target the user should still be offered (spec 1 §6).
     */
    sourceRowId: string;
    /** The row that was linked, for the sourcing-context comparison. */
    sourceRow: LinkableCustomIngredient;
    candidate: SwapCandidate;
    /** Quantity the user confirmed on the source row, used when a target's own is unusable. */
    appliedQuantity: number;
    analyses: ScannableAnalysis[];
    subrecipes: ScannableSubrecipe[];
    /** How a prep is marked in the list — applying there also changes every dish using it. */
    prepLabel: (name: string) => string;
}

/**
 * Every OTHER live place the same custom ingredient is referenced: dish rows
 * (`custom === true` in the analysis JSONB) and prep rows
 * (`is_custom` on `subrecipe_ingredients`). Matched on canonical product identity,
 * never on names. Each match carries ITS OWN quantity converted into the target's
 * unit, so a 15 g usage and a 200 g usage do not end up identical.
 *
 * Pure: the caller supplies the live data.
 */
export function findCustomIngredientUses({
    identityKey,
    sourceItemId,
    sourceRowId,
    sourceRow,
    candidate,
    appliedQuantity,
    analyses,
    subrecipes,
    prepLabel,
}: FindCustomIngredientUsesParams): CustomLinkMatch[] {
    const targetUnit = candidateUnit(candidate);
    const matches: CustomLinkMatch[] = [];

    const push = (
        kind: CustomLinkItemKind,
        itemId: string,
        itemName: string,
        row: LinkableCustomIngredient & { id: string },
        index: number
    ) => {
        const reviewIngredient = toReviewIngredient(row);
        // Nothing to apply where the row already points at the chosen candidate.
        if (alreadyOnCandidate(reviewIngredient, candidate)) return;
        const converted = defaultLinkQuantity(row.quantityNeeded, row.quantityInfo?.stdUnit, targetUnit);
        matches.push({
            kind,
            rowId: row.id,
            itemId,
            itemName,
            ingredientIndex: index,
            ingredient: reviewIngredient,
            currentMatchName: row.productName,
            quantity: converted > 0 ? converted : appliedQuantity,
        });
    };

    analyses.forEach(analysis => {
        analysis.ingredients.forEach((row, index) => {
            if (analysis.id === sourceItemId && row.id === sourceRowId) return;
            if (!row.custom) return;
            if (!sameSourcingContext(row, sourceRow)) return;
            // A row carrying a reference is not a plain custom row: pre-M35 analyses can
            // still hold an INLINED subrecipe copy that has both `custom` and
            // `subrecipeId`, and both cost twins price such a row as the whole subrecipe
            // portion (costPriceUtils.ts:481, dishCostCalc.ts:316). Re-pointing it would
            // write to a row nothing reads as an ingredient.
            if (row.subrecipeId || row.nestedCostPriceId) return;
            if (linkIdentityKey(row) !== identityKey) return;
            push('costPrice', analysis.id, analysis.costPriceName, row, index);
        });
    });

    subrecipes.forEach(subrecipe => {
        subrecipe.ingredients.forEach((row, index) => {
            if (subrecipe.id === sourceItemId && row.id === sourceRowId) return;
            if (!row.isCustom) return;
            if (row.nestedSubrecipeId) return;
            if (!sameSourcingContext(row, sourceRow)) return;
            if (linkIdentityKey(row) !== identityKey) return;
            push('subrecipe', subrecipe.id, prepLabel(subrecipe.name), row, index);
        });
    });

    return matches;
}

/**
 * The linked replacement for a dish (JSONB) row. The row is REPLACED, not merged:
 * `custom`, `customPlatformName` and `alternatives` are dropped (the row is an
 * order-history row now, and the server strips alternatives from custom rows so a
 * linked row correctly starts with none). `wastePercentage` is carried over.
 */
export function linkedCostPriceRow(
    row: CostPriceIngredient,
    candidate: SwapCandidate,
    quantity: number
): CostPriceIngredient {
    if (candidate.kind === 'subrecipe') {
        return { id: row.id, subrecipeId: candidate.subrecipeId, subrecipePortionsNeeded: quantity,
            ...(row.automationTemplateKey && { automationTemplateKey: row.automationTemplateKey }) };
    }
    return {
        id: row.id,
        ...(row.automationTemplateKey && { automationTemplateKey: row.automationTemplateKey }),
        articleId: candidate.articleId,
        productName: candidate.displayName,
        platform: candidate.platform,
        quantityNeeded: quantity,
        quantityInfo: candidate.quantityInfo,
        wastePercentage: row.wastePercentage,
        // Sourcing link deliberately dropped, exactly as `handleSwapAlternative` does when it
        // re-points a row (`useCostPriceDetail.ts:889-891`). The picker only ever offers this
        // restaurant's products and subrecipes (`SwapIngredientPanel.tsx:66,68` — it has no
        // sibling-sourcing path at all), so keeping the link would price a current-restaurant
        // article against a sibling's order history.
        sourceRestaurantId: undefined,
        sourceRestaurantName: undefined,
    };
}

/**
 * The linked replacement for a prep row. `is_custom` is a relational column here,
 * so the row is rewritten with `isCustom: false` rather than by dropping a flag
 * from a JSONB blob.
 *
 * Referencing dishes are deliberately NOT rewritten. The folder README's trap 4
 * names `updateAnalysesForSubrecipe()` as the thing that re-inlines a prep into every
 * dish; that function no longer exists anywhere in `server/` or `frontend/src`
 * (M35 replaced the inlined copies with references —
 * `server/database/sql/M35_nested_subrecipes.sql:7-9`). `PUT /api/subrecipes/update`
 * (`server/routes/subrecipesRoutes.ts:161-175`) writes only `subrecipes` +
 * `subrecipe_ingredients`. A dish's subrecipe row is a bare reference that BOTH cost
 * twins re-resolve live on every render — `costPriceUtils.ts:481-497` and
 * `dishCostCalc.ts:316-327` — so the change lands in dishes with no write at all, and
 * rewriting them here would be a double-apply.
 */
export function linkedSubrecipeRow(
    row: SubrecipeIngredient,
    candidate: SwapCandidate,
    quantity: number
): SubrecipeIngredient {
    if (candidate.kind === 'subrecipe') {
        return { id: row.id, nestedSubrecipeId: candidate.subrecipeId, nestedSubrecipePortionsNeeded: quantity };
    }
    return {
        id: row.id,
        articleId: candidate.articleId,
        productName: candidate.displayName,
        platform: candidate.platform,
        quantityNeeded: quantity,
        quantityInfo: candidate.quantityInfo,
        isCustom: false,
        wastePercentage: row.wastePercentage,
    };
}

/**
 * Strip the local id — the same normalization as `useSubrecipeDetail.toIngredientInput`,
 * repeated here so this module stays importable without pulling in a React hook file.
 * Spread-based rather than field-whitelisted (like `toIngredientUpdatePayload`), so a new
 * ingredient field is preserved instead of silently dropped. Worth consolidating into
 * `subrecipesService` if a third caller appears.
 */
function toSubrecipeIngredientInput(row: SubrecipeIngredient): SubrecipeIngredientInput {
    if (row.nestedSubrecipeId) {
        return {
            nestedSubrecipeId: row.nestedSubrecipeId,
            nestedSubrecipePortionsNeeded: row.nestedSubrecipePortionsNeeded ?? 0,
        };
    }
    const { id, ...rest } = row;
    return { ...rest, alternatives: row.isCustom ? undefined : row.alternatives };
}

export interface CustomLinkAdapterConfig {
    /** The row the user clicked Link on. Null identity = no fan-out can be offered. */
    source: {
        itemId: string;
        /** Row id — only this row is excluded from the fan-out, not its whole item. */
        rowId: string;
        identityKey: string | null;
        /** The row itself, for the sourcing-context comparison. */
        row: LinkableCustomIngredient;
    };
    /**
     * Writes the SOURCE row. Domain-specific on purpose: the dish editor persists
     * immediately (its every edit does), while the subrecipe editor is a local draft
     * that only persists on Save — so it mutates the draft here instead.
     */
    applyToSource: (candidate: SwapCandidate, quantity: number) => Promise<void>;
    /**
     * Live cost model to scan for other uses. Read as getters, not arrays: the
     * adapter instance must stay stable for the life of the picker (it remembers
     * which domain each fan-out target belongs to), yet the scan has to see the
     * store as it is when the user confirms.
     */
    getAnalyses: () => ScannableAnalysis[];
    getSubrecipes: () => ScannableSubrecipe[];
    prepLabel: (name: string) => string;
    /**
     * Whether to offer the post-swap "apply to the other places too" fan-out (default true).
     *
     * FALSE when the source write is not persistent. Fan-out targets are written immediately,
     * so pairing them with a draft-local source lets a user link inside a prep, apply to three
     * dishes, then hit Cancel — the dishes are changed, the prep is not, and nothing says so.
     * Suppressing the offer costs no capability: the same fan-out is reachable from any dish
     * that uses the ingredient (where it also covers the prep), and spec 2's bulk flow covers
     * every usage in one commit.
     */
    offerApplyElsewhere?: boolean;
}

/**
 * Adapter for `SwapIngredientPanel` + `ApplyToOtherItemsPanel`, used verbatim.
 * `allowedSwapKinds` omits 'custom', which drops the Custom Ingredients tab AND
 * excludes custom ingredients from the Suggested list — linking a custom onto
 * another custom is the one thing this flow exists to stop.
 */
export function createCustomLinkAdapter(config: CustomLinkAdapterConfig): ReviewWriteAdapter {
    // Populated by findSameIngredientElsewhere, read back by swap: the panels hand
    // back only (itemId, ingredientIndex), and a cost price id and a subrecipe id are
    // both bare uuids, so the domain of a fan-out target has to be remembered here.
    const refs = new Map<string, CustomLinkMatch>();
    const refKey = (itemId: string, index: number) => `${itemId}#${index}`;

    const notSupported = (method: string) => async () => {
        throw new Error(`customLinkAdapter.${method} is not part of the link flow`);
    };

    return {
        allowedSwapKinds: ['product', 'subrecipe'],

        remove: notSupported('remove'),
        setQuantity: notSupported('setQuantity'),

        async swap(itemId, _ingredient, ingredientIndex, candidate, quantity) {
            const ref = refs.get(refKey(itemId, ingredientIndex));
            if (!ref) {
                // The source row. The panel is mounted with ingredientIndex -1, so this
                // can never be confused with a fan-out target.
                await config.applyToSource(candidate, quantity);
                return;
            }

            if (ref.kind === 'costPrice') {
                const analysis = await fetchCostPriceAnalysisById(ref.itemId);
                const target = resolveRow(analysis.ingredients, ref, row => !!row.custom && !row.subrecipeId && !row.nestedCostPriceId);
                if (!target) throw new Error(`Custom ingredient row not found in analysis ${ref.itemId}`);
                const ingredients = analysis.ingredients
                    .map(row => (row.id === target.id ? linkedCostPriceRow(row, candidate, quantity) : row))
                    .map(toIngredientUpdatePayload);
                const updated = await updateCostPriceAnalysis({ id: ref.itemId, expectedUpdatedAt: recipeWriteVersion(analysis), ingredients });
                useCostPriceStore.getState().upsertOne(updated);
                return;
            }

            const subrecipe = await fetchSubrecipeById(ref.itemId);
            const target = resolveRow(subrecipe.ingredients, ref, row => !!row.isCustom && !row.nestedSubrecipeId);
            if (!target) throw new Error(`Custom ingredient row not found in subrecipe ${ref.itemId}`);
            const ingredients = subrecipe.ingredients
                .map(row => (row.id === target.id ? linkedSubrecipeRow(row, candidate, quantity) : row))
                .map(toSubrecipeIngredientInput);
            await updateSubrecipe({ id: ref.itemId, expectedUpdatedAt: recipeWriteVersion(subrecipe), ingredients });
            // Reloading the subrecipes store is enough for the dishes too: a dish's
            // subrecipe rows are references that `enrichCostPriceWithCurrentPrices`
            // resolves live out of `allSubrecipes` on every render (post-M35 there is no
            // server-side re-inlining pass). Same idiom as the subrecipe editor's Save.
            await useSubrecipesStore.getState().reload();
        },

        findSameIngredientElsewhere(_templateName, sourceItemId, candidate, appliedQuantity) {
            if (config.offerApplyElsewhere === false) return [];
            if (!config.source.identityKey) return [];
            const matches = findCustomIngredientUses({
                identityKey: config.source.identityKey,
                sourceItemId,
                sourceRowId: config.source.rowId,
                sourceRow: config.source.row,
                candidate,
                appliedQuantity,
                analyses: config.getAnalyses(),
                subrecipes: config.getSubrecipes(),
                prepLabel: config.prepLabel,
            });
            refs.clear();
            matches.forEach(match => refs.set(refKey(match.itemId, match.ingredientIndex), match));
            return matches;
        },
    };
}

/**
 * Re-resolve the row a match pointed at against freshly fetched data: by id first,
 * then by position, then by identity. Row ids are regenerated on every write, so a
 * concurrent edit elsewhere can invalidate all three — returning undefined then is
 * correct, and the caller surfaces it as a failed apply rather than writing the
 * wrong row.
 */
function resolveRow<T extends LinkableCustomIngredient & { id: string }>(
    rows: T[],
    ref: CustomLinkMatch,
    isCustomRow: (row: T) => boolean
): T | undefined {
    const byId = rows.find(row => row.id === ref.rowId);
    if (byId && isCustomRow(byId)) return byId;
    const byIndex = rows[ref.ingredientIndex];
    if (byIndex && isCustomRow(byIndex) && linkIdentityKey(byIndex) === linkIdentityKey(ref.ingredient)) {
        return byIndex;
    }
    return rows.find(row => isCustomRow(row) && linkIdentityKey(row) === linkIdentityKey(ref.ingredient));
}

/**
 * A `ReviewWriteAdapter` that WRITES NOTHING — it resolves the picked candidate upward
 * instead. The bulk flow (spec 2) is plan-only until its single commit, so the shared
 * picker's write-through assumption has to be broken exactly once; doing it here keeps
 * `SwapIngredientPanel` unforked and unchanged.
 *
 * `findSameIngredientElsewhere` returns `[]` on purpose: the bulk review already groups
 * every usage of a custom ingredient into one row, so there is no "apply elsewhere" to
 * offer — and the fan-out panel writes immediately, which a plan-only flow must not do.
 */
export function createCaptureOnlyLinkAdapter(
    onPick: (candidate: SwapCandidate, quantity: number) => void
): ReviewWriteAdapter {
    const notSupported = (method: string) => async () => {
        throw new Error(`captureOnlyLinkAdapter.${method} is not part of the bulk link flow`);
    };
    return {
        allowedSwapKinds: ['product', 'subrecipe'],
        remove: notSupported('remove'),
        setQuantity: notSupported('setQuantity'),
        async swap(_itemId, _ingredient, _index, candidate, quantity) {
            onPick(candidate, quantity);
        },
        findSameIngredientElsewhere: () => [],
    };
}

/** What the bulk flow's intro step states before spending any AI budget. */
export interface CustomIngredientUsageSummary {
    /** Distinct custom ingredients actually referenced by the cost model. */
    used: number;
    /** Dish rows referencing one. */
    dishUsages: number;
    /** Prep rows referencing one. */
    subrecipeUsages: number;
    /** Custom ingredients referenced by nothing — out of scope, reported and skipped. */
    orphans: number;
}

/**
 * Count what a run would analyse, from live store data, so the intro can be honest before
 * the (paid) pass. Grouped by canonical identity — one bottle used on 9 dishes is ONE
 * decision, not nine, which is the same grouping the server plan uses.
 *
 * Pure: the caller supplies the data.
 */
export function summarizeCustomIngredientUsage(params: {
    analyses: ScannableAnalysis[];
    subrecipes: ScannableSubrecipe[];
    /** The restaurant's `custom_ingredients` rows, for the orphan count. */
    customIngredients: LinkableCustomIngredient[];
}): CustomIngredientUsageSummary {
    const usedKeys = new Set<string>();
    let dishUsages = 0;
    let subrecipeUsages = 0;

    params.analyses.forEach(analysis => analysis.ingredients.forEach(row => {
        if (!row.custom || row.subrecipeId || row.nestedCostPriceId) return;
        const key = linkIdentityKey(row);
        if (!key) return;
        usedKeys.add(key);
        dishUsages += 1;
    }));

    params.subrecipes.forEach(subrecipe => subrecipe.ingredients.forEach(row => {
        if (!row.isCustom || row.nestedSubrecipeId) return;
        const key = linkIdentityKey(row);
        if (!key) return;
        usedKeys.add(key);
        subrecipeUsages += 1;
    }));

    const orphans = params.customIngredients.filter(ci => {
        const key = linkIdentityKey(ci);
        return !key || !usedKeys.has(key);
    }).length;

    return { used: usedKeys.size, dishUsages, subrecipeUsages, orphans };
}
